import dayjs from "dayjs";
import { DateRangeValue, DealsDateRange, DealStage, DealStageGroup, DealStageItem, isDealStageGroup } from "store/types";

// Relative presets are stored as JUST their key — their dates are (re)generated from the current date at
// request time, so a saved "Last Calendar Quarter" always means the quarter relative to *now*, never the
// dates that were current when it was picked. Absolute presets keep the specific dates the user chose.
const RELATIVE_DATE_RANGE_KEYS = new Set([
  "lastCalendarMonth",
  "lastCalendarQuarter",
  "lastYear",
  "ytd",
  "last3Years",
  "last5Years",
  "fromInception"
]);

/** What we persist for the pipeline diff date range: relative → key only; absolute → key + picked dates. */
export const dateRangeForStorage = (value: DateRangeValue): DateRangeValue =>
  RELATIVE_DATE_RANGE_KEYS.has(value.value) ? { value: value.value } : value;

/**
 * Resolve a stored diff date-range selection into concrete ISO `{ from, to }` for the search request.
 * Relative presets are computed from the current date; absolute presets use their stored dates. Returns
 * `undefined` when there's nothing to send (no selection, or an absolute range without a concrete `to`).
 */
export const resolveDiffDateRange = (value: DateRangeValue | null | undefined): DealsDateRange | undefined => {
  if (!value) return undefined;
  const now = dayjs();
  // Plain calendar days (YYYY-MM-DD) — no time-of-day / timezone offset in the request.
  const ymd = (date: dayjs.Dayjs) => date.format("YYYY-MM-DD");

  switch (value.value) {
    case "lastCalendarMonth": {
      const month = now.subtract(1, "month");
      return { from: ymd(month.startOf("month")), to: ymd(month.endOf("month")) };
    }
    case "lastCalendarQuarter": {
      const quarter = Math.floor(now.month() / 3);
      const lastQuarterMonth = quarter === 0 ? 9 : (quarter - 1) * 3;
      const year = quarter === 0 ? now.year() - 1 : now.year();
      const start = dayjs().year(year).month(lastQuarterMonth).startOf("month");
      const end = dayjs()
        .year(year)
        .month(lastQuarterMonth + 2)
        .endOf("month");
      return { from: ymd(start), to: ymd(end) };
    }
    case "lastYear": {
      const year = now.subtract(1, "year");
      return { from: ymd(year.startOf("year")), to: ymd(year.endOf("year")) };
    }
    case "ytd":
      return { from: ymd(now.startOf("year")), to: ymd(now) };
    case "last3Years":
      return { from: ymd(now.subtract(3, "years")), to: ymd(now) };
    case "last5Years":
      return { from: ymd(now.subtract(5, "years")), to: ymd(now) };
    case "fromInception":
      return { from: null, to: ymd(now) };
    default:
      // Absolute range (dateRange / selectYear / selectQuarter): use the picked dates as plain days.
      return value.to ? { from: value.from ? ymd(dayjs(value.from)) : null, to: ymd(dayjs(value.to)) } : undefined;
  }
};

/** Asset class is a plain string on the card; guard against null/undefined. */
export const getAssetClassName = (assetClass: DealStageItem["asset_class"] | null | undefined): string =>
  assetClass ? String(assetClass) : "";

export interface CardChip {
  key: string;
  value: string;
}

/**
 * Resolve the chip values for a card from the requested `card_characteristics` names.
 * Each name is a top-level key on the deal whose value is string | string[] | null — a multi-value
 * characteristic yields one chip per element. Values are read verbatim from the payload.
 */
export const getCardChips = (item: DealStageItem, names: string[]): CardChip[] => {
  const record = item as unknown as Record<string, unknown>;
  const chips: CardChip[] = [];

  names.forEach((name) => {
    const value = record[name];
    if (Array.isArray(value)) {
      value.forEach((entry, index) => {
        const text = String(entry).trim();
        if (text) chips.push({ key: `${name}-${index}`, value: text });
      });
    } else if (value !== null && value !== undefined && String(value).trim() !== "") {
      chips.push({ key: name, value: String(value) });
    }
  });

  return chips;
};

/** Stable key for a group inside a stage, from its group-title path — a deal can live in several groups. */
export const groupKeyPath = (stageId: number, titlePath: string[]): string => `${stageId}:${titlePath.join(">")}`;

/** Encode a group title path for use inside a draggableId (a deal can appear in several groups → unique ids). */
export const encodeGroupPath = (path: string[]): string => path.map(encodeURIComponent).join(">");

/** Every group key (incl. nested) rendered for a stage — used to Collapse All. */
export const collectGroupKeys = (stage: DealStage): string[] => {
  const keys: string[] = [];
  const walk = (nodes: Array<DealStageGroup | DealStageItem>, parentPath: string[]) => {
    nodes.forEach((node) => {
      if (isDealStageGroup(node)) {
        const path = [...parentPath, node.group_title];
        keys.push(groupKeyPath(stage.id, path));
        walk(node.children, path);
      }
    });
  };
  walk(stage.groups ?? [], []);
  return keys;
};
